// ComplianceTracking.tsx
import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { PoliciesService } from 'services';
import * as uuid from 'uuid';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import { useNavigate } from 'react-router-dom';

export interface IEvidence {
  '#': number;
  'ERL #': string;
  'Area of Focus': string;
  'Artifact Description': string;
  'SCF Control Mappings': string;
  'Documentation Artifact': string;
}

interface IFramework {
  'SCF #': string;
  Frameworks: { [key: string]: string[] };
  'Evidence Request List': IEvidence[];
}

export interface ComplianceFrameworkScfMappings {
  scf_framework_key: any;
}

export interface ComplianceFrameworks {
  id: string;
  name: string;
  is_active: boolean;
  compliance_framework_scf_mappings: ComplianceFrameworkScfMappings;
}

export interface PolicyComplianceFramework {
  framework_id: string;
  compliance_frameworks: ComplianceFrameworks;
}

export interface IPolicyFramework {
  id: string;
  policy_compliance_frameworks: PolicyComplianceFramework[];
}

interface IVersion {
  version: string;
  evidenceList: IEvidence[];
  reference: string;
}

interface IFrameworkTracking {
  scfFrameworkKey: string;
  reference: string;
  versions: IVersion[];
}

export interface IEvidenceTracking {
  _id: string;
  organizationId: string;
  completed_by: string;
  updatedBy: any;
  erl_number: string;
  is_completed: boolean;
  completed_at: string;
  notes: any;
  isActive: boolean;
  created_at: string;
  updated_at: string;
  __v: number;
}

interface InventoryRowProps {
  label: string;
  value: string | number;
}

const InventoryRow = ({ label, value }: InventoryRowProps): ReactElement => (
  <Stack
    direction="row"
    justifyContent="space-between"
    alignItems="center"
    className="inventory-row"
    sx={{ py: 1.5 }}
  >
    <Typography
      className="inventory-row-label"
      sx={{ color: '#6b7280', fontSize: '14px' }}
    >
      {label}
    </Typography>
    <Typography
      className="inventory-row-value"
      sx={{ fontWeight: 600, color: '#052921', fontSize: '16px' }}
    >
      {value}
    </Typography>
  </Stack>
);

const ComplianceTracking = (): ReactElement => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [frameworks, setFrameworks] = useState<IFramework[]>([]);
  const [policyFrameworks, setPolicyFrameworks] = useState<IPolicyFramework[]>(
    []
  );
  const [frameworkTrackingList, setFrameworkTrackingList] = useState<
    IFrameworkTracking[]
  >([]);
  const [evidenceTrackings, setEvidenceTrackings] = useState<
    IEvidenceTracking[]
  >([]);

  // Helper methods
  const totalFrameworkEvidences = (
    frameworkTracking: IFrameworkTracking
  ): number =>
    frameworkTracking.versions.reduce(
      (prev, current) => prev + current.evidenceList.length,
      0
    );

  const totalEvidenceFromFramework = (
    frameworkTracking: IFrameworkTracking
  ): number =>
    frameworkTracking.versions.reduce(
      (prev, current) =>
        prev +
        evidenceTrackings.filter(
          (eT) =>
            current.evidenceList
              .map((el) => el['ERL #'])
              .includes(eT.erl_number) && eT.is_completed
        ).length,
      0
    );

  // Computed values
  const uniqueEvidences = useMemo(
    () =>
      frameworkTrackingList.reduce(
        (prev, current) => prev + totalFrameworkEvidences(current),
        0
      ),
    [frameworkTrackingList]
  );

  const evidenceCovered = useMemo(
    () =>
      frameworkTrackingList.reduce(
        (prev, current) => prev + totalEvidenceFromFramework(current),
        0
      ),
    [frameworkTrackingList, evidenceTrackings]
  );

  const evidenceCoverPercent = useMemo(() => {
    if (uniqueEvidences === 0) return 0;
    const percent = (evidenceCovered / uniqueEvidences) * 100;
    return Math.ceil(percent);
  }, [uniqueEvidences, evidenceCovered]);

  const activeFrameworksCount = frameworkTrackingList.length;

  // Prepare framework tracking data
  const prepareFrameworkTracking = (): void => {
    let scfFrameworkKeys: string[] = [];
    policyFrameworks.forEach((pf) => {
      const keys = pf.policy_compliance_frameworks.map(
        (p) =>
          p.compliance_frameworks.compliance_framework_scf_mappings
            .scf_framework_key
      );
      scfFrameworkKeys = [...scfFrameworkKeys, ...keys];
    });

    const frameworkTrackings: IFrameworkTracking[] = [];
    scfFrameworkKeys = Array.from(new Set(scfFrameworkKeys));

    for (let i = 0; i < scfFrameworkKeys.length; i += 1) {
      const scfFrameworkKey = scfFrameworkKeys[i];
      const frameworkTracking: IFrameworkTracking = {
        scfFrameworkKey,
        versions: [],
        reference: uuid.v4(),
      };

      for (let j = 0; j < frameworks.length; j += 1) {
        const framework = frameworks[j];
        if (
          framework.Frameworks &&
          Object.hasOwn(framework.Frameworks, scfFrameworkKey)
        ) {
          const versions = framework.Frameworks[scfFrameworkKey];
          for (let k = 0; k < versions.length; k += 1) {
            const version = versions[k];
            if (framework['Evidence Request List'].length > 0) {
              frameworkTracking.versions.push({
                version,
                evidenceList: framework['Evidence Request List'],
                reference: uuid.v4(),
              });
            }
          }
        }
      }
      frameworkTrackings.push(frameworkTracking);
    }
    setFrameworkTrackingList(frameworkTrackings);
  };

  // API Calls
  const loadPolicyComplianceTracking = (): void => {
    PoliciesService.policyCompliancTracking()
      .then((response) => {
        const { data } = response.data;
        setPolicyFrameworks(data.policies);
      })
      .catch((error) => console.log(error))
      .finally(() => {
        setIsLoading(false);
      });
  };

  const getEvidenceComplianceTracking = (): void => {
    PoliciesService.getComplianceTracking()
      .then((response) => {
        const { data } = response.data;
        setEvidenceTrackings(data.tracking);
      })
      .catch((error) => console.log(error))
      .finally(() => {
        setIsLoading(false);
      });
  };

  const loadFrameworkDetails = (): void => {
    setIsLoading(true);
    fetch('/staticjson/PolicyFramework.json', {
      method: 'GET',
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP error! ${res.status}`);
        return res.json();
      })
      .then((data) => {
        setFrameworks(data);
        loadPolicyComplianceTracking();
      })
      .catch((err) => {
        console.error('Fetch error:', err);
        setIsLoading(false);
      });
  };

  // Hooks
  useEffect(() => {
    loadFrameworkDetails();
    getEvidenceComplianceTracking();
  }, []);

  useEffect(() => {
    if (frameworks.length > 0 && policyFrameworks.length > 0) {
      prepareFrameworkTracking();
    }
  }, [frameworks, policyFrameworks]);

  if (isLoading) {
    return (
      <Box
        className="empty-content"
        sx={{ display: 'flex', justifyContent: 'center', p: 4 }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="compliance-tracking-wrapper">
      <Card variant="outlined">
        <CardHeader
          title="Compliance Tracking"
          subheader="Overview of evidence collection across frameworks"
          sx={{
            '& .MuiCardHeader-title': {
              fontSize: '18px',
              fontWeight: 600,
              color: '#052921',
            },
            '& .MuiCardHeader-subheader': {
              fontSize: '14px',
              color: '#6b7280',
            },
          }}
          action={
            <IconButton
              onClick={() => {
                navigate(`/compliance-tracking`);
              }}
            >
              <RemoveRedEyeIcon />
            </IconButton>
          }
        />
        <CardContent>
          <Stack spacing={2}>
            <Card variant="outlined" sx={{ borderRadius: '8px' }}>
              <CardHeader
                className="inventory-card-header"
                title={
                  <Typography
                    sx={{
                      fontSize: '16px',
                      fontWeight: 600,
                      color: '#052921',
                    }}
                  >
                    Evidence Collection
                  </Typography>
                }
                sx={{ pb: 0 }}
              />
              <CardContent>
                <Stack direction="column" gap={0.5}>
                  <InventoryRow
                    label="Overall Progress"
                    value={`${evidenceCoverPercent}%`}
                  />
                  <InventoryRow
                    label="Evidence Collected"
                    // value={`${evidenceCovered} of ${uniqueEvidences}`}
                    value={`${evidenceCovered} of 10`}
                  />
                  <InventoryRow
                    label="Active Frameworks"
                    value={activeFrameworksCount}
                  />
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ComplianceTracking;
